import RoomServiceOutlinedIcon from '@mui/icons-material/RoomServiceOutlined'
import VolumeOffOutlinedIcon from '@mui/icons-material/VolumeOffOutlined'
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined'
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useState } from 'react'

import { useAvisosCocinaOptional } from '../contexts/avisosCocinaContext.ts'
import { useAhora } from '../hooks/useAhora.ts'
import AvisoCocinaItem from './AvisoCocinaItem.tsx'

interface AvisosCocinaBellProps {
  /**
   * true: botón flotante abajo a la izquierda (lo usa la pila cuando está minimizada).
   * false: botón normal para ubicarlo en una barra de la vista, dentro de AvisosCocinaProvider.
   */
  flotante?: boolean
}

/**
 * Campana de pedidos listos con la lista completa en un popover.
 * No muestra nada fuera de AvisosCocinaProvider o si no hay avisos.
 */
const AvisosCocinaBell = ({ flotante = false }: AvisosCocinaBellProps) => {
  const ctx = useAvisosCocinaOptional()
  const ahora = useAhora()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)

  if (!ctx || ctx.avisos.length === 0) return null
  const { avisos, procesando, entregar, entregarTodos, mostrar, sonido, setSonido } = ctx

  const boton = (
    <Tooltip title="Pedidos listos en cocina">
      <IconButton onClick={(e) => setAnchor(e.currentTarget)} aria-label="pedidos listos en cocina">
        <Badge badgeContent={avisos.length} color="success" max={99}>
          <RoomServiceOutlinedIcon color={flotante ? 'success' : undefined} />
        </Badge>
      </IconButton>
    </Tooltip>
  )

  return (
    <>
      {flotante ? (
        <Paper
          elevation={6}
          sx={{
            position: 'fixed',
            left: 16,
            bottom: 'max(16px, env(safe-area-inset-bottom, 0px))',
            // Debajo de diálogos (1300) y toasts
            zIndex: 1250,
            borderRadius: '50%',
          }}
        >
          {boton}
        </Paper>
      ) : (
        boton
      )}
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={
          flotante ? { vertical: 'top', horizontal: 'left' } : { vertical: 'bottom', horizontal: 'right' }
        }
        transformOrigin={
          flotante ? { vertical: 'bottom', horizontal: 'left' } : { vertical: 'top', horizontal: 'right' }
        }
        slotProps={{ paper: { sx: { width: { xs: 'calc(100vw - 32px)', sm: 380 } } } }}
      >
        <Stack direction="row" sx={{ alignItems: 'center', px: 2, py: 1 }}>
          <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 600 }}>
            Pedidos listos ({avisos.length})
          </Typography>
          <Tooltip title={sonido ? 'Silenciar' : 'Activar sonido'}>
            <IconButton size="small" onClick={() => setSonido(!sonido)}>
              {sonido ? (
                <VolumeUpOutlinedIcon fontSize="small" />
              ) : (
                <VolumeOffOutlinedIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Stack>
        <Divider />
        <Stack sx={{ px: 2, py: 1.25, gap: 1.25, maxHeight: 360, overflowY: 'auto' }}>
          {avisos.map((aviso) => (
            <AvisoCocinaItem
              key={aviso.key}
              aviso={aviso}
              ahora={ahora}
              ocupado={procesando.has(aviso.key)}
              onEntregar={(a) => void entregar(a)}
            />
          ))}
        </Stack>
        <Divider />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1, py: 0.5 }}>
          <Button
            size="small"
            onClick={() => {
              mostrar()
              setAnchor(null)
            }}
          >
            Mostrar en pantalla
          </Button>
          <Button
            size="small"
            color="success"
            onClick={() => void entregarTodos()}
            disabled={procesando.size > 0}
          >
            Entregar todos
          </Button>
        </Box>
      </Popover>
    </>
  )
}

export default AvisosCocinaBell
